import { useState } from "react";
import { api } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import PaymentBrick from "../components/PaymentBrick";
import TrustBadges from "../components/TrustBadges";
import { Gift, ArrowRight } from "lucide-react";

const MIN_AMOUNT = 100;

const maskCpf = (v) => {
  const d = (v || "").replace(/\D/g, "").slice(0, 11);
  if (d.length <= 3) return d;
  if (d.length <= 6) return `${d.slice(0, 3)}.${d.slice(3)}`;
  if (d.length <= 9) return `${d.slice(0, 3)}.${d.slice(3, 6)}.${d.slice(6)}`;
  return `${d.slice(0, 3)}.${d.slice(3, 6)}.${d.slice(6, 9)}-${d.slice(9)}`;
};
const isValidCpf = (raw) => {
  const c = (raw || "").replace(/\D/g, "");
  if (c.length !== 11 || /^(\d)\1{10}$/.test(c)) return false;
  for (const j of [9, 10]) {
    let total = 0;
    for (let i = 0; i < j; i++) total += parseInt(c[i], 10) * (j + 1 - i);
    let d = (total * 10) % 11;
    if (d === 10) d = 0;
    if (d !== parseInt(c[j], 10)) return false;
  }
  return true;
};

function formatBRL(digits) {
  const cents = parseInt(digits || "0", 10);
  const value = cents / 100;
  return value.toLocaleString("pt-BR", { style: "currency", currency: "BRL", minimumFractionDigits: 2 });
}

export default function GiftCardPage() {
  const { user } = useAuth();
  const [amountDigits, setAmountDigits] = useState("");
  const [form, setForm] = useState({
    recipient_name: "", recipient_email: "", sender_name: "", message: "",
    buyer_email: "", buyer_name: "", buyer_cpf: "",
  });
  const [loading, setLoading] = useState(false);
  const [step, setStep] = useState("form"); // form | payment | approved | pending
  const [intent, setIntent] = useState(null);
  const [error, setError] = useState("");
  const [paymentResult, setPaymentResult] = useState(null);

  const amountFloat = parseInt(amountDigits || "0", 10) / 100;
  const isValidAmount = amountFloat >= MIN_AMOUNT;
  const showMinError = amountDigits.length > 0 && !isValidAmount;

  const handleAmountChange = (e) => {
    setAmountDigits(e.target.value.replace(/\D/g, "").slice(0, 9));
  };

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    if (!isValidAmount) return;
    const buyer_email = (user?.email || form.buyer_email).trim().toLowerCase();
    const buyer_name = (user?.name || form.buyer_name || form.sender_name).trim();
    const buyer_cpf = form.buyer_cpf || maskCpf(user?.cpf || "");
    if (!buyer_email) { setError("Informe seu e-mail."); return; }
    if (!buyer_name) { setError("Informe seu nome."); return; }
    if (!isValidCpf(buyer_cpf)) { setError("CPF inválido. Verifique os 11 dígitos."); return; }
    setLoading(true);
    try {
      const r = await api.post("/mp/checkout/init", {
        kind: "giftcard",
        cpf: buyer_cpf.replace(/\D/g, ""),
        customer_email: buyer_email,
        customer_name: buyer_name,
        giftcard_amount: amountFloat,
        giftcard_recipient_name: form.recipient_name,
        giftcard_recipient_email: form.recipient_email,
        giftcard_message: form.message,
        giftcard_sender_name: form.sender_name || buyer_name,
      });
      setIntent(r.data);
      setStep("payment");
    } catch (err) {
      setError(err?.response?.data?.detail || "Erro ao iniciar o checkout.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen" data-testid="giftcard-page">
      <section className="bg-[#FAF9F7] py-20">
        <div className="max-w-3xl mx-auto px-6 text-center">
          <Gift size={32} className="text-[#B8A88A] mx-auto mb-5" />
          <p className="overline text-[#A8A8A8] mb-3">Gift Card Prize Joias</p>
          <h1 className="font-display text-4xl md:text-5xl mb-4 leading-tight">
            O presente perfeito existe.<br/>É só deixar quem você ama escolher.
          </h1>
          <p className="font-italic-display text-lg text-[#6B6B6B]">Entrega digital por e-mail. Sem prazo de uso.</p>
        </div>
      </section>

      <section className="max-w-3xl mx-auto px-6 py-16">
        {step === "form" && (
          <form onSubmit={submit} className="space-y-10">
            <div className="text-center">
              <label htmlFor="gift-amount" className="overline text-[#A8A8A8] mb-6 block tracking-[0.25em]">
                Qual valor você gostaria de presentear?
              </label>
              <div className={`relative mx-auto max-w-md border-b-2 transition-colors ${showMinError ? "border-[#B85050]" : "border-[#E8E4DF] focus-within:border-[#B8A88A]"}`}>
                <div className="font-display text-5xl md:text-6xl text-center py-4 text-[#1A1A1A] leading-none select-none pointer-events-none">
                  {amountDigits ? formatBRL(amountDigits) : <span className="text-[#D4CCBE]">R$ 0,00</span>}
                </div>
                <input
                  id="gift-amount" type="text" inputMode="numeric" autoComplete="off"
                  value={amountDigits} onChange={handleAmountChange}
                  className="absolute inset-0 w-full h-full opacity-0 cursor-text"
                  data-testid="gift-amount-input" aria-label="Valor do gift card"
                />
              </div>
              <p className={`text-sm mt-3 transition-opacity ${showMinError ? "text-[#B85050] opacity-100" : "text-[#A8A8A8] opacity-80"}`}>
                {showMinError ? "O valor mínimo para um Gift Card é de R$ 100,00" : "Toque acima e digite o valor — mínimo R$ 100,00."}
              </p>
            </div>

            <div className="grid md:grid-cols-2 gap-5">
              <div>
                <label className="overline text-[#A8A8A8] mb-2 block">Nome de quem recebe</label>
                <input data-testid="gift-recipient-name" value={form.recipient_name}
                  onChange={(e) => setForm({ ...form, recipient_name: e.target.value })}
                  className="w-full border border-[#E8E4DF] p-3 focus:outline-none focus:border-[#B8A88A]" />
              </div>
              <div>
                <label className="overline text-[#A8A8A8] mb-2 block">E-mail de quem recebe</label>
                <input type="email" data-testid="gift-recipient-email" value={form.recipient_email}
                  onChange={(e) => setForm({ ...form, recipient_email: e.target.value })}
                  className="w-full border border-[#E8E4DF] p-3 focus:outline-none focus:border-[#B8A88A]"
                  placeholder="amor@exemplo.com" />
              </div>
            </div>

            <div className="grid md:grid-cols-2 gap-5">
              <div>
                <label className="overline text-[#A8A8A8] mb-2 block">Seu nome</label>
                <input data-testid="gift-sender-name"
                  value={user?.name || form.sender_name}
                  onChange={(e) => setForm({ ...form, sender_name: e.target.value })}
                  disabled={!!user?.name}
                  className="w-full border border-[#E8E4DF] p-3 focus:outline-none focus:border-[#B8A88A] disabled:bg-[#FAF9F7]" />
              </div>
              {!user && (
                <div>
                  <label className="overline text-[#A8A8A8] mb-2 block">Seu e-mail</label>
                  <input type="email" required
                    value={form.buyer_email}
                    onChange={(e) => setForm({ ...form, buyer_email: e.target.value })}
                    className="w-full border border-[#E8E4DF] p-3 focus:outline-none focus:border-[#B8A88A]" />
                </div>
              )}
            </div>

            <div>
              <label className="overline text-[#A8A8A8] mb-2 block">Seu CPF (para emissão da nota fiscal)</label>
              <input value={form.buyer_cpf || maskCpf(user?.cpf || "")}
                onChange={(e) => setForm({ ...form, buyer_cpf: maskCpf(e.target.value) })}
                inputMode="numeric"
                placeholder="000.000.000-00"
                className="w-full border border-[#E8E4DF] p-3 focus:outline-none focus:border-[#B8A88A]" data-testid="gift-buyer-cpf"/>
            </div>

            <div>
              <label className="overline text-[#A8A8A8] mb-2 block">Mensagem personalizada (opcional)</label>
              <textarea data-testid="gift-message" rows={3} maxLength={200}
                value={form.message}
                onChange={(e) => setForm({ ...form, message: e.target.value })}
                className="w-full border border-[#E8E4DF] p-3 focus:outline-none focus:border-[#B8A88A]"
                placeholder="Para celebrar tudo o que você é..." />
              <p className="text-xs text-[#A8A8A8] mt-1">{form.message.length}/200</p>
            </div>

            {error && <p className="text-sm text-[#A8503B]">{error}</p>}

            <button type="submit" disabled={loading || !isValidAmount}
              className="btn-accent w-full disabled:opacity-50 disabled:cursor-not-allowed" data-testid="gift-submit">
              {loading ? "Processando..." : "Continuar para pagamento"} <ArrowRight size={14} className="ml-2" />
            </button>
          </form>
        )}

        {step === "payment" && intent && (
          <div className="bg-white p-6 md:p-8">
            <h2 className="font-display text-2xl mb-3">Pagamento</h2>
            <p className="text-sm text-[#6B6B6B] mb-6">
              Cartão de crédito, PIX ou boleto. Total: <strong>R$ {intent.amount.toFixed(2).replace(".", ",")}</strong>.
            </p>
            <PaymentBrick
              intentId={intent.intent_id} amount={intent.amount}
              publicKey={intent.public_key} payerEmail={intent.payer_email}
              onApproved={(r) => { setPaymentResult(r); setStep("approved"); }}
              onPending={(r) => { setPaymentResult(r); setStep("pending"); }}
              onRejected={() => setError("Pagamento recusado. Tente novamente.")}
            />
          </div>
        )}

        {step === "approved" && (
          <div className="bg-white p-10 text-center">
            <div className="w-12 h-px bg-[#B8A88A] mx-auto mb-6"/>
            <h2 className="font-display text-3xl mb-3">Gift Card emitido!</h2>
            <p className="text-[#4A4A4A]">Você e o destinatário recebem o código por e-mail em instantes.</p>
          </div>
        )}

        {step === "pending" && (
          <div className="bg-white p-10 text-center space-y-4">
            <h2 className="font-display text-3xl mb-3">Quase lá</h2>
            {paymentResult?.pix_qr_code_base64 && (
              <img src={`data:image/png;base64,${paymentResult.pix_qr_code_base64}`} alt="PIX" className="mx-auto w-56 h-56"/>
            )}
            {paymentResult?.boleto_url && (
              <a href={paymentResult.boleto_url} target="_blank" rel="noopener noreferrer" className="btn-primary inline-flex">Abrir boleto</a>
            )}
          </div>
        )}

        <div className="mt-10 bg-white p-6">
          <h3 className="overline text-[#A8A8A8] mb-4">Compra segura</h3>
          <TrustBadges variant="stack" />
        </div>
      </section>
    </div>
  );
}
